import { type SearchCategory } from '@kijiji/generated/graphql-types'
import { useTranslation } from 'next-i18next'
import { type FC, useMemo } from 'react'

import { CategoryDropdownContainer } from '@/components/shared/global-header/search-bar/category-dropdown/styled'
import { getIconFromCategoryId } from '@/components/shared/global-header/search-bar/category-dropdown/utils'
import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { isValidCategory } from '@/domain/category/isValidCategory'
import { type SelectItem, Select } from '@/ui/molecules/select'

export type CategoryDropdownProps = {
  categories: SearchCategory[]
  handleChangeCategory: (category: SearchCategory) => void
  isMobileHeaderExpanded: boolean
  selectedCategory: number
}

export const CategoryDropdown: FC<CategoryDropdownProps> = ({
  categories = [],
  handleChangeCategory,
  selectedCategory,
  ...rest
}) => {
  const { t } = useTranslation(['global_header', 'common'])

  const handleCategorySelect = async (item?: SelectItem) => {
    const categoryIdString = `${item?.value}`

    if (!isValidCategory(parseInt(categoryIdString))) {
      return
    }

    const categoryId = parseInt(`${item?.value}`)
    let selectedCategory = categories.find((category) => category.id === categoryId)

    /**
     * Manually set category if failed to find in the list
     * shouldn't ever happen since user always chooses one from the list
     * */
    if (!selectedCategory) {
      selectedCategory = {
        id: categoryId,
        localizedName: item?.label ?? '',
      }
    }
    handleChangeCategory(selectedCategory)
  }

  const options = useMemo(
    () =>
      categories.map((item) => {
        /**
         * Category 0 localized name is returned by BE as Classifieds.
         * That is the only category label we will programmatically alter from BE to say "All Categories".
         */
        const categoryName =
          item.id === ALL_CATEGORIES_ID_NUM
            ? t('common:categories.all_categories')
            : item.localizedName
        const categoryIcon = getIconFromCategoryId(item.id)

        return {
          label: categoryName,
          value: item.id,
          icon: categoryIcon,
        }
      }),
    [categories, t]
  )

  return (
    <CategoryDropdownContainer {...rest}>
      <Select
        bottom="0"
        selectedValue={selectedCategory}
        data-testid="category-dropdown"
        id="category-dropdown"
        label={t('global_header:search_bar.categories_label')}
        onSelectChange={handleCategorySelect}
        options={options}
      />
    </CategoryDropdownContainer>
  )
}
